/**
 * Shared field-widget primitives — Phase 4.4d.iv.
 *
 * Every `<keystone-field-*>` widget shares the same outer structure:
 *
 *   <div class="field" data-error="<bool>">
 *     <label for="input">{label}{required ? "*" : ""}</label>
 *     <slot name="input" />            ← the actual input element
 *     <p class="error" hidden|visible>{error}</p>
 *   </div>
 *
 * Putting the wrapper styles in one place keeps the widgets focused on
 * input behaviour. The CSS deliberately mirrors the Tailwind-vocabulary
 * approach used in client/src/keystone.css (Phase 4.4d.ii) so a future
 * `@apply`-based migration stays mechanical.
 *
 * This file exports `fieldStyles` (a Lit `css` template) plus a tiny
 * `humaniseFieldName` helper used when no `label` attribute is supplied.
 */

import { css } from "lit";

export const fieldStyles = css`
  :host {
    display: block;
    margin: 0.75rem 0;
    font-family:
      system-ui,
      -apple-system,
      "Segoe UI",
      Roboto,
      sans-serif;
    color: #1a1a1a;
  }

  label {
    display: block;
    font-weight: 600;
    margin-bottom: 0.25rem;
    color: #2a5a8c;
    font-size: 0.875rem;
  }

  .required-marker {
    color: #b91c1c; /* tw red-700 */
    margin-left: 0.125rem;
  }

  input,
  select,
  textarea {
    width: 100%;
    padding: 0.5rem 0.625rem;
    border: 1px solid #d1d5db; /* tw gray-300 */
    border-radius: 0.375rem;
    font-size: 1rem;
    font-family: inherit;
    color: inherit;
    background: #fff;
    box-sizing: border-box;
  }

  input:focus,
  select:focus,
  textarea:focus {
    outline: none;
    border-color: #2a5a8c;
    box-shadow: 0 0 0 3px rgba(42, 90, 140, 0.15);
  }

  input[type="checkbox"] {
    width: auto;
    margin-right: 0.5rem;
    vertical-align: middle;
  }

  :host([data-error]) input,
  :host([data-error]) select,
  :host([data-error]) textarea {
    border-color: #b91c1c;
  }

  :host([data-error]) input:focus,
  :host([data-error]) select:focus,
  :host([data-error]) textarea:focus {
    box-shadow: 0 0 0 3px rgba(185, 28, 28, 0.15);
  }

  .error {
    color: #b91c1c;
    font-size: 0.875rem;
    margin: 0.25rem 0 0;
  }

  .checkbox-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .checkbox-row label {
    margin-bottom: 0;
    font-weight: 400;
  }
`;

/**
 * Convert a snake_case or camelCase field name into a human-readable
 * label. Used as a fallback when no `label` attribute is supplied.
 *
 *   contact_email → "Contact email"
 *   vatNumber     → "Vat number"
 *   name          → "Name"
 */
export function humaniseFieldName(name: string): string {
  const spaced = name
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

/**
 * Lowercase + dehyphenate the standard set of attribute aliases each
 * field widget accepts. Centralised so we have one source of truth for
 * "what props does a field widget take".
 *
 * Every field widget exposes these properties; type-specific extensions
 * are declared on the subclass.
 */
export interface KeystoneFieldBaseProps {
  name: string;
  label: string;
  value: string;
  required: boolean;
  error: string | null;
  disabled: boolean;
}
